import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    Search, Brain, ArrowLeft, FileText, ExternalLink
} from 'lucide-react'
import { mockQuestionBanks } from '@/data/mentor-mock-data'
import { toast } from 'sonner'

export function StudentQuestionBankView() {
    const [selectedBankId, setSelectedBankId] = useState<string | null>(null)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedSubject, setSelectedSubject] = useState<string>('all')

    // Filter published banks
    const availableBanks = mockQuestionBanks.filter(bank => {
        const matchesSearch = bank.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            bank.chapter.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesSubject = selectedSubject === 'all' || bank.subject === selectedSubject
        const isPublished = bank.status === 'published'

        return matchesSearch && matchesSubject && isPublished
    })

    const subjects = ['all', 'Physics', 'Chemistry', 'Biology']

    // If a bank is selected, show the file view
    if (selectedBankId) {
        const bank = mockQuestionBanks.find(b => b.id === selectedBankId)
        if (!bank) return null

        return (
            <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
                <div className="flex items-center gap-2 mb-4">
                    <button
                        onClick={() => setSelectedBankId(null)}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 text-theme-secondary" />
                    </button>
                    <div>
                        <h2 className="text-xl font-bold text-theme-primary">{bank.title}</h2>
                        <p className="text-sm text-theme-secondary">{bank.subject} • {bank.chapter}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    {/* Main Content: File Preview / Download */}
                    <div className="md:col-span-2 space-y-4">
                        <GlassmorphicCard className="p-8 flex flex-col items-center justify-center text-center min-h-[400px]">
                            <div className="w-20 h-20 bg-primary-blue/10 rounded-full flex items-center justify-center mb-6">
                                <FileText className="h-10 w-10 text-primary-blue" />
                            </div>
                            <h3 className="text-xl font-bold text-theme-primary mb-2">Question Bank File</h3>
                            <p className="text-theme-secondary max-w-md mb-8">
                                This question bank is available as a searchable file.
                                Click the button below to view the questions.
                            </p>

                            <div className="flex gap-4">
                                <button
                                    onClick={() => toast.success('Opening file preview...')}
                                    className="px-6 py-2.5 bg-white dark:bg-slate-800 text-theme-secondary border border-slate-200 dark:border-slate-700 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    Preview File
                                </button>
                            </div>
                        </GlassmorphicCard>
                    </div>

                    {/* Right: Bank Info */}
                    <div className="space-y-4">
                        <GlassmorphicCard className="p-4">
                            <h3 className="font-bold text-theme-primary mb-3">Bank Details</h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center">
                                    <span className="text-theme-secondary">Subject</span>
                                    <span className="font-medium text-theme-primary">{bank.subject}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-theme-secondary">Chapter</span>
                                    <span className="font-medium text-theme-primary">{bank.chapter}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-theme-secondary">Questions</span>
                                    <span className="font-medium text-theme-primary">{bank.questionCount}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-theme-secondary">Uploaded</span>
                                    <span className="font-medium text-theme-primary">{bank.createdAt}</span>
                                </div>
                            </div>
                        </GlassmorphicCard>

                        <GlassmorphicCard className="p-4 bg-orange-50 dark:bg-orange-900/10 border-orange-200 dark:border-orange-800/30">
                            <h3 className="font-bold text-orange-700 dark:text-orange-400 mb-2">Instructions</h3>
                            <ul className="text-xs text-orange-600 dark:text-orange-300 space-y-1 list-disc pl-4">
                                <li>Use the visual file viewer to browse questions.</li>
                                <li>Zoom controls are available in the viewer.</li>
                                <li>Contact mentor for specific doubts.</li>
                            </ul>
                        </GlassmorphicCard>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                    <h2 className="text-xl md:text-2xl font-bold text-theme-primary flex items-center gap-2">
                        <Brain className="h-6 w-6 md:h-7 md:w-7 text-primary-blue" />
                        Question Bank
                    </h2>
                    <p className="text-sm text-theme-secondary mt-1">Access question bank files uploaded by mentors</p>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-theme-secondary" />
                    <input
                        type="text"
                        placeholder="Search topics or chapters..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm focus:border-primary-blue outline-none"
                    />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1">
                    {subjects.map(sub => (
                        <button
                            key={sub}
                            onClick={() => setSelectedSubject(sub)}
                            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${selectedSubject === sub
                                ? 'bg-primary-blue text-white'
                                : 'bg-white dark:bg-slate-800 text-theme-secondary border border-slate-200 dark:border-slate-700'
                                }`}
                        >
                            {sub === 'all' ? 'All Subjects' : sub}
                        </button>
                    ))}
                </div>
            </div>

            {/* Banks List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableBanks.map((bank, idx) => (
                    <motion.div
                        key={bank.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                    >
                        <GlassmorphicCard className="p-0 overflow-hidden hover:shadow-lg transition-all group cursor-pointer h-full flex flex-col">
                            <div className={`h-2 ${bank.subject === 'Physics' ? 'bg-blue-500' :
                                    bank.subject === 'Chemistry' ? 'bg-green-500' :
                                        'bg-purple-500'
                                }`} />
                            <div className="p-5 flex-1 flex flex-col" onClick={() => setSelectedBankId(bank.id)}>
                                <div className="flex justify-between items-start mb-3">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${bank.subject === 'Physics' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30' :
                                            bank.subject === 'Chemistry' ? 'bg-green-100 text-green-700 dark:bg-green-900/30' :
                                                'bg-purple-100 text-purple-700 dark:bg-purple-900/30'
                                        }`}>
                                        {bank.subject}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-bold ${bank.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                                bank.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                                    'bg-amber-100 text-amber-700'
                                            }`}>
                                            {bank.difficulty}
                                        </span>
                                    </div>
                                </div>
                                <h3 className="font-bold text-lg text-theme-primary mb-1 group-hover:text-primary-blue transition-colors line-clamp-2">
                                    {bank.title}
                                </h3>
                                <p className="text-sm text-theme-secondary mb-4">{bank.chapter}</p>

                                <div className="mt-auto flex items-center justify-between text-xs text-theme-secondary pt-4 border-t border-slate-100 dark:border-slate-800">
                                    <div className="flex items-center gap-1.5">
                                        <FileText className="h-3.5 w-3.5" />
                                        <span>PDF File</span>
                                    </div>
                                    <ExternalLink className="h-4 w-4 transform group-hover:-translate-y-1 transition-transform text-primary-blue" />
                                </div>
                            </div>
                        </GlassmorphicCard>
                    </motion.div>
                ))}
            </div>

            {availableBanks.length === 0 && (
                <div className="text-center py-12">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="h-8 w-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-medium text-theme-primary">No question banks found</h3>
                    <p className="text-theme-secondary mt-1">Try adjusting your search or filters</p>
                </div>
            )}
        </div>
    )
}
